import React, { PureComponent, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { tableHelper } from '@builttocreate/engine-utils';
import isEqual from 'lodash/isEqual';

import { useVirtualizer } from '@tanstack/react-virtual';
import TableFieldHeader from './TableFieldHeader';
import TableFieldRow from './TableFieldRow';
import FieldTitle from '../FieldTitle';
import handleTableFieldPageBreakAdjustments from '../../../../lib/tableFieldPageBreakManager';

import FieldTableCustomColumnIds from '../../../../constants/FieldTableCustomColumnIds';
import FieldTableColumnTypes from '../../../../constants/FieldTableColumnTypes';
import FieldModes from '../../../../constants/FieldModes';

import Tooltip from '../../../misc/Tooltip';
import Button from '../../../misc/Button';

import ThemeIcons from '../../../../theme/Icons';

const {
  generateTableLookup,
  generateTableLookupForRows,
  getRows,
  sortRows,
  getColumns,
  sortColumns,
  getCleanedRowOrder
} = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}
`;

const StyledTable = styled.div`
  display: inline-block;
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => props.border};

  ${(props) => {
    if (props.measure) {
      return 'overflow: hidden;';
    } else {
      return 'overflow: auto;';
    }
  }}

  #table-field-row:first-child div {
    border-top: 0px;
  }

  #table-field-row:last-child div {
    border-bottom: 0px;
  }

  #table-field-row div div:first-child {
    border-left: 0px;
  }

  #table-field-row div div:last-child {
    border-right: 0px;
  }

`;

const StyledVirtualizedTable = styled(StyledTable)`
  width: 100%;
  height: 100%;

  ${(props) => {
    if (props.measure) {
      return 'overflow: hidden;';
    } else {
      return 'overflow: auto;';
    }
  }}

`;

const StyledTooltipWrapper = styled.span`
 position:absolute;
 right:0;
 top:0;
 z-index:999999999;
`;

class TableField extends PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    width: PropTypes.number,
    disabled: PropTypes.bool,
    fieldMode: PropTypes.string,
    virtual: PropTypes.bool,
    editable: PropTypes.bool,

    value: PropTypes.array, //Row data
    rowOrder: PropTypes.array,

    tableColumns: PropTypes.array,
    tableColumnOrder: PropTypes.array,

    //Page Breaks Helpers
    pageBreakPointsPixels: PropTypes.array,

    //SDK Styles
    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,

    //Cell styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    height: PropTypes.number,

    //Column Action Handlers
    onColumnEdit: PropTypes.func,
    onColumnMoveLeft: PropTypes.func,
    onColumnMoveRight: PropTypes.func,

    //Row Action Handlers
    onRowCreate: PropTypes.func,
    onRowDuplicate: PropTypes.func,
    onRowMoveUp: PropTypes.func,
    onRowMoveDown: PropTypes.func,
    onRowAddBelow: PropTypes.func,
    onRowDelete: PropTypes.func,

    //Cell Action Handlers
    onCellChange: PropTypes.func,
    onCellUpload: PropTypes.func,

    //Used for calculating width and height of element
    measure: PropTypes.bool,
    onMeasureCompleted: PropTypes.func

  }

  constructor(props) {
    super(props);
    this.state = {
      resizing: false,
      rows: [],
      columns: [],
      rowTableLookup: {},
      tableMap: [],
      focused: '',

      //Used for measuring
      rowsLoadCompleted: false,
      measureCompleted: false,
    };
  }

  componentDidMount() {
    this.populateTableData();
  }

  componentDidUpdate(prevProps) {

    const { columnWidths, rowsLoadCompleted, measureCompleted } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      width,
      value,
      rowOrder,
      tableColumns,
      tableColumnOrder,
      onMeasureCompleted
    } = this.props;

    if (!tableColumnOrder || !tableColumns) return;

    if (
      tableColumns !== prevProps.tableColumns
      || tableColumnOrder !== prevProps.tableColumnOrder
      || rowOrder !== prevProps.rowOrder
      || value !== prevProps.value //rows
    ) {
      this.populateTableData();
    }

    if (
      width !== prevProps.width
      || tableColumnOrder.length !== prevProps.tableColumnOrder.length
      || !isEqual(tableColumns, prevProps.tableColumns)
    ) {
      this.setState({ resizing: true, columnWidths: null }, () => {
        this.setState({ resizing: false });
      });
    }

    if (onMeasureCompleted && !measureCompleted && columnWidths && rowsLoadCompleted && this.containerRef) {

      //Used to prevent onMeasureCompleted from being fired more than once.
      this.setState({ measureCompleted: true });

      onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, this.containerRef.getBoundingClientRect());

    }

  }

  populateTableData = () => {

    const { value, rowOrder, tableColumns, tableColumnOrder } = this.props;

    const formattedRows = this.getFormattedAndSortedRows(value, rowOrder);
    const formattedColumns = this.getFormattedAndSortedColumns(tableColumns, tableColumnOrder);

    const tableLookup = generateTableLookup(formattedRows, formattedColumns);
    const rowTableLookup = generateTableLookupForRows(formattedRows, formattedColumns, tableLookup);

    this.setState({
      rows: formattedRows,
      columns: formattedColumns,
      rowTableLookup,
      tableMap: this.getTableNavigationMap(formattedRows, formattedColumns)
    });

  }

  static defaultProps = {}

  render() {

    const { columns, columnWidths, rows, rowTableLookup, focused, resizing } = this.state;
    const {
      fieldId,
      fieldPositionId,
      width,
      disabled,
      fieldMode,
      virtual,
      measure,
      editable,
      isMobileView,
      height,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      pageBreakPointsPixels,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleFontStyle,
      titleFontWeight,
      titleTextAlign,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextAlign,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,

      //Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,

    } = this.props;

    if (resizing) return <div>Loading</div>;

    const TableNode = virtual ? VirtualizedTable : Table;

    return (
      <StyledWrapper
        measure={measure}
        data-testid={`${fieldPositionId}-table-field`}
        ref={(e) => {
          this.containerRef = e;
        }}
      >
        <FieldTitle
          {...{
            title,
            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleTextAlign,
            titleFontStyle,
            titleFontWeight,
            titleTextTransform,
            titleTextDecoration,
          }}
        />
        <TableNode
          id={fieldPositionId}
          disabled={disabled}
          fieldMode={fieldMode}
          measure={measure}
          focused={focused}
          columns={columns}
          rows={rows}
          rowTableLookup={rowTableLookup}
          editable={editable}
          pageBreakPointsPixels={pageBreakPointsPixels}
          {...{
            width,
            columnWidths,

            primaryColor,
            dangerColor,

            columnTitleFontSize,
            columnTitleFontColor,
            columnTitleFontStyle,
            columnTitleFontWeight,
            columnTitleTextAlign,
            columnTitleTextTransform,
            columnTitleTextDecoration,
            columnTitleBackgroundColor,
            columnTitlePadding,

            //Styles
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            height,

            fieldId,
            tipTitle,
            tipDescription,
            tipVisible,

            isMobileView
          }}
          onColumnEdit={this.handleColumnEdit}
          onColumnMoveLeft={this.handleColumnMoveLeft}
          onColumnMoveRight={this.handleColumnMoveRight}
          onColumnRefs={this.handleColumnRefs}
          onCellChange={this.handleCellChange}
          onCellUpload={this.handleCellUpload}
          onCellFocus={this.handleCellFocus}
          onCellKeyPress={this.handleCellKeyPress}
          onCellBlur={this.handleCellBlur}
          onRowCreate={this.handleRowCreate}
          onRowAddBelow={this.handleRowAddBelow}
          onRowDuplicate={this.handleRowDuplicate}
          onRowMoveUp={this.handleRowMoveUp}
          onRowMoveDown={this.handleRowMoveDown}
          onRowDelete={this.handleRowDelete}
          onRowLoadCompleted={this.handleRowLoadCompleted}
        />
      </StyledWrapper>
    );

  }

  handleRowLoadCompleted = (rowIndex) => {

    const { rows, rowsLoadCompleted } = this.state;

    if (!this.loaded) this.loaded = {};
    this.loaded[rowIndex] = true;

    /**
     * Only update measurements once all fields have been measured.
     */
    if (!rowsLoadCompleted && Object.keys(this.loaded).length === rows.length) {
      this.setState({ rowsLoadCompleted: true });
    }

  }

  handleRowCreate = () => {

    const { onRowCreate, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const { rows } = this.state;

    if (onRowCreate) onRowCreate(fieldId, fieldIdentifier, fieldPositionId, rows.length - 1);

  }

  handleRowAddBelow = (rowId, rowIndex) => {

    const { onRowAddBelow, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowAddBelow) onRowAddBelow(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowDuplicate = (rowId, rowIndex) => {

    const { onRowDuplicate, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onRowDuplicate) onRowDuplicate(fieldId, fieldIdentifier, fieldPositionId, rowId, rowIndex);

  }

  handleRowMoveUp = (rowId, rowIndex) => {

    const { onRowMoveUp, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rowIndex === 0) return;

    if (onRowMoveUp) onRowMoveUp(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowMoveDown = (rowId, rowIndex) => {

    const { rows } = this.state;

    const { onRowMoveDown, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rows.length === 1 || (rows.length - 1) === rowIndex) return;

    if (onRowMoveDown) onRowMoveDown(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleRowDelete = (rowId, rowIndex) => {

    const { rows } = this.state;

    const { onRowDelete, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (rows.length === 1) return;

    if (onRowDelete) onRowDelete(fieldId, fieldIdentifier, fieldPositionId, rowIndex);

  }

  handleColumnRefs = (columnRefs) => {

    const { columnWidths } = this.state;
    if (columnWidths) return;

    const widths = {};
    columnRefs.forEach((colRef) => widths[colRef.columnId] = colRef.e?.offsetWidth);

    this.setState({ columnWidths: widths });

  }

  handleColumnEdit = (columnId) => {
    const { onColumnEdit, fieldId, fieldPositionId, fieldIdentifier } = this.props;
    if (onColumnEdit) onColumnEdit(fieldId, fieldIdentifier, fieldPositionId, columnId);
  }

  handleColumnMoveRight = (columnId) => {
    const { onColumnMoveRight, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onColumnMoveRight) onColumnMoveRight(fieldId, fieldIdentifier, fieldPositionId, columnId);
  }

  handleColumnMoveLeft = (columnId) => {
    const { onColumnMoveLeft, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onColumnMoveLeft) onColumnMoveLeft(fieldId, fieldIdentifier, fieldPositionId, columnId);
  }

  handleCellChange = (rowId, columnId, value) => {

    const { onCellChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onCellChange) onCellChange(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value);

  }

  handleCellUpload = (rowId, columnId, value) => {
    const { onCellUpload, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onCellUpload) onCellUpload(fieldId, fieldIdentifier, fieldPositionId, rowId, columnId, value);
  }

  handleCellFocus = (rowId, columnId, columnIdentifier, columnType, e) => {

    const { focused } = this.state;

    const { onFocus, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const focusedKey = `${rowId}_${columnId}`;

    /**<---------------------------NOTICE------------------------------>
     * Why do we use columnTypes here to trigger onFocus?
     *
     * We use columnTypes to trigger onFocus only when the focused components of a tableField
     * (ie StyledSelect/StyledTextArea) have been rendered so that when user triggers e.blur
     * within the blurFunction of onFocus event we can the blur these focused components.
     * We will only have access to the focused component's event object when they are rendered.
     *
     * Table cells use two sets of components to display data.
     * 1) If a cell is focused StyledSelect or StyledTextarea is used.
     * 2) If a cell is not focused a simple tableDataCell is used.
     *
     * handleCellFocus will be triggered twice everytime a cell is focused.
     *
     * The First call to handleCellFocus:
     * 1. When a table cell is focused for the first time,  TableDataCell will be clicked triggering
     *    handleCellFocus and the focused cell's rowId and columnId  will be set as the focused in state.
     *    ie: this.setState({ focused: `${rowId}_${columnId}` });
     *
     * 2. We should NOT TRIGGER onFocus during the first call. If we do so, we won't be able to blur
     *    the StyledSelect/StyledTextarea when user triggers e.blur since we would not have access to
     *    the event object associated with StyledSelect/StyledTextarea as they are yet to be rendered.
     *
     * 3. Once focused is set in state StyledTextArea or StyledSelectCell will replace TableDataCell.
     *
     * The Second call to handleCellFocus:
     * 1. Once StyledSelect or StyledTextarea is rendered autoFocus prop on them triggers handleCellFocus again.
     * 2. We will only pass columnType to handleCellFocus now and we SHOULD TRIGGER onFocus during this since
     *    we will be able to blur these focused components using e.target.blur when user triggers e.blur from the
     *    blurFunction due to the availablity of the event object associated with StyledSelect/StyledTextarea
     *
     */

    if (onFocus && columnType) {

      const blurFunction = e && e.blur ? {
        blur: () => {
          e.blur();
        }
      } : {};

      onFocus(fieldId, fieldIdentifier, fieldPositionId, blurFunction, rowId, columnId, columnIdentifier);

    }

    if (focusedKey === focused) return;

    this.setState({ focused: `${rowId}_${columnId}` });

  }

  handleCellBlur = (rowId, columnId, columnIdentifier) => {

    const { onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    this.setState({ focused: null });

    if (onBlur) {

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, columnId, columnIdentifier);

    }

  }

  getFormattedAndSortedColumns = (columns, columnOrder) => {

    const { disabled, fieldMode } = this.props;

    const nextColumns = getColumns(sortColumns(columns, columnOrder));

    // If first column is not rows settings column, then add it
    if (!disabled && fieldMode !== FieldModes.edit && nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowMenu) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowMenu,
        type: FieldTableCustomColumnIds.rowMenu,
        width: 20,
      });
    }

    // If first column is not rows settings column, then add it
    if (nextColumns[0] && nextColumns[0]._id !== FieldTableCustomColumnIds.rowNumber) {
      nextColumns.unshift({
        _id: FieldTableCustomColumnIds.rowNumber,
        type: FieldTableCustomColumnIds.rowNumber,
        width: 46,
      });
    }

    return nextColumns;

  }

  getFormattedAndSortedRows = (rows, rowOrder) => {

    /**
     * getCleanedRowOrder returns a cleanedRowOrder after removing duplicate rowIds/ deletedRowIds.
     * If the rowOrder is passed as an empty array, we would generate a rowOrder based on
     * the rows that were passed via the input.
     */

    const cleanedRowOrder = getCleanedRowOrder(rowOrder, rows);

    return sortRows(getRows(rows), cleanedRowOrder);

  }

  handleCellKeyPress = (rowId, columnId, event) => {

    const { focused, tableMap } = this.state;

    const keyCode = event.keyCode || event.charCode;

    // Shift Tab key
    if (keyCode === 9 && event.shiftKey) {

      event.preventDefault();
      event.target.blur();

      const currentCellIndex = tableMap.indexOf(focused);

      if (currentCellIndex === 0) return;

      const nextCellIndex = tableMap[currentCellIndex - 1];

      this.setState({ focused: nextCellIndex });

    }

    // Tab key
    else if (keyCode === 9) {

      event.preventDefault();
      event.target.blur();

      const currentCellIndex = tableMap.indexOf(focused);

      if (currentCellIndex === (tableMap.length - 1)) return;

      const nextCellIndex = tableMap[currentCellIndex + 1];

      this.setState({ focused: nextCellIndex });
    }

    // escape
    else if (keyCode === 27) {

      event.preventDefault();
      event.target.blur();

      this.setState({ focused: null });

    }

  }

  getTableNavigationMap = (rows, columns) => {

    const tableMaps = [];

    rows.forEach((row) => {
      columns.forEach((column) => {
        if (column.type === FieldTableColumnTypes.dropdown && column._id !== 'rowMenu') {
          tableMaps.push(`${row._id}_${column._id}`);
        } else if (column.type === FieldTableColumnTypes.text && column._id !== 'rowNumber') {
          tableMaps.push(`${row._id}_${column._id}`);
        } else if (column.type === FieldTableColumnTypes.image && column._id !== 'rowNumber') {
          tableMaps.push(`${row._id}_${column._id}`);
        }
      });
    });

    return tableMaps;

  }

}

const VirtualizedTable = ({

  id,
  disabled,
  fieldMode,
  editable,
  isMobileView,
  measure,
  focused,
  rows,
  columns,
  columnWidths = {},
  rowTableLookup,
  fieldId,
  tipVisible,
  tipTitle,
  tipDescription,

  primaryColor,
  dangerColor,

  //Column title styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Styles
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderWidth,
  borderRadius,
  backgroundColor,
  padding,

  //Actions
  onColumnRefs,
  onColumnEdit,
  onColumnMoveLeft,
  onColumnMoveRight,
  onCellUpload,
  onCellChange,
  onCellFocus,
  onCellBlur,
  onCellKeyPress,
  onRowCreate,
  onRowAddBelow,
  onRowDuplicate,
  onRowMoveUp,
  onRowMoveDown,
  onRowDelete,
  onRowLoadCompleted,

}) => {

  const parentRef = useRef();

  let totalRowLength = rows.length + 1; //We do +1 to include the table header
  if (!disabled) totalRowLength += 1; //Add +1 more for the table footer (Add Row Button)

  const rowVirtualizer = useVirtualizer({
    count: totalRowLength,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
  });

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const border = `${borderWidth}px solid ${borderColor}`;

  const handleRowAddBelow = useCallback((rowId, rowIndex) => {

    onRowAddBelow(rowId, rowIndex);

  }, [rows]);

  const handleRowMoveUp = useCallback((rowId, rowIndex) => {

    onRowMoveUp(rowId, rowIndex);

  }, [rows]);

  const handleRowMoveDown = useCallback((rowId, rowIndex) => {

    onRowMoveDown(rowId, rowIndex, { align: 'start' });

  }, [rows]);

  const handleDuplicateRow = useCallback((rowId, rowIndex) => {

    onRowDuplicate(rowId, rowIndex);

  }, [rows]);

  const handleRowDelete = useCallback((rowId, rowIndex) => {

    onRowDelete(rowId, rowIndex);

  }, [rows]);

  const getTooltip = () => {

    if (disabled) return null;

    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );
  };

  return (
    <>
      {getTooltip()}
      <StyledVirtualizedTable
        data-testid={`${id}-table-field-table-virtual`}
        ref={parentRef}
        {...{ border, borderRadius, measure }}
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {

            /**
             * Why do we subtract 1 here?
             *
             * We subtract 1 because we have included in the header
             * for the virtual rows. That means index 0 is actually
             * the header and not a row.
             *
             * When we go to access the actual row data to start displaying
             * valid rows we need to start at 0. Since virtualRow.index starts
             * the actual row data at 1, due to header posiion, we subtract
             * so that even though virtualRow.index is 1 we can still access
             * the first row in the rows array at index 0;
             */
            const rowIndex = (virtualRow.index - 1);
            const row = rows[rowIndex];

            const firstRow = virtualRow.index === 0;
            const lastRow = virtualRow.index === (totalRowLength - 1);

            let displayRow = null;

            if (firstRow) {
              displayRow = (
                <TableFieldHeader
                  {...{
                    id,
                    disabled,
                    columns,
                    columnWidths,
                    editable,

                    //Styles
                    fontSize: columnTitleFontSize,
                    fontColor: columnTitleFontColor,
                    textAlign: columnTitleTextAlign,
                    fontStyle: columnTitleFontStyle,
                    fontWeight: columnTitleFontWeight,
                    textTransform: columnTitleTextTransform,
                    textDecoration: columnTitleTextDecoration,
                    backgroundColor: columnTitleBackgroundColor,
                    padding: columnTitlePadding,
                    borderWidth,
                    borderColor,
                  }}
                  onColumnRefs={onColumnRefs}
                  onEdit={onColumnEdit}
                  onMoveLeft={onColumnMoveLeft}
                  onMoveRight={onColumnMoveRight}
                />
              );
            } else if (!disabled && fieldMode !== FieldModes.edit && lastRow) {
              displayRow = (
                <Button text="Add Row" styles={{ margin: '8px' }} icon={ThemeIcons.misc.add} onClick={onRowCreate} />
              );
            } else {
              displayRow = !row || !columnWidths ? null : (
                <TableFieldRow
                  key={`${row._id}-${rowIndex}`}
                  tableLookup={rowTableLookup[rowIndex + 1] || {}}
                  {...{
                    id,
                    disabled,
                    isMobileView,
                    focused,
                    row,
                    rowIndex,
                    columns,
                    columnWidths,

                    //Styles
                    primaryColor,
                    dangerColor,
                    fontSize,
                    fontColor,
                    fontStyle,
                    fontWeight,
                    textAlign,
                    textTransform,
                    textDecoration,
                    textOverflow,
                    borderColor,
                    borderWidth,
                    backgroundColor,
                    padding,
                  }}
                  onCellChange={onCellChange}
                  onCellFocus={onCellFocus}
                  onCellBlur={onCellBlur}
                  onCellKeyPress={onCellKeyPress}
                  onCellUpload={onCellUpload}
                  onRowAddBelow={handleRowAddBelow}
                  onRowDuplicate={handleDuplicateRow}
                  onRowMoveUp={handleRowMoveUp}
                  onRowMoveDown={handleRowMoveDown}
                  onRowDelete={handleRowDelete}
                  onLoadCompleted={onRowLoadCompleted}
                  textOverflow={textOverflow}
                />
              );
            }

            return (
              <div
                id="table-field-row"
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {displayRow}
              </div>
            );

          })}
        </div>
      </StyledVirtualizedTable>
    </>
  );
};

const Table = ({

  id,
  disabled,
  fieldMode,
  isMobileView,
  editable,
  measure,
  focused,
  rows,
  columns,
  columnWidths = {},
  rowTableLookup,
  pageBreakPointsPixels,
  fieldId,
  tipVisible,
  tipTitle,
  tipDescription,

  primaryColor,
  dangerColor,

  //Column title styles
  columnTitleFontSize,
  columnTitleFontColor,
  columnTitleFontStyle,
  columnTitleFontWeight,
  columnTitleTextAlign,
  columnTitleTextTransform,
  columnTitleTextDecoration,
  columnTitleBackgroundColor,
  columnTitlePadding,

  //Styles
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderWidth,
  borderRadius,
  backgroundColor,
  padding,

  //Actions
  onColumnRefs,
  onColumnEdit,
  onColumnMoveLeft,
  onColumnMoveRight,
  onCellChange,
  onCellFocus,
  onCellBlur,
  onCellKeyPress,
  onCellUpload,
  onRowCreate,
  onRowAddBelow,
  onRowDuplicate,
  onRowMoveUp,
  onRowMoveDown,
  onRowDelete,
  onRowLoadCompleted

}) => {

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const border = `${borderWidth}px solid ${borderColor}`;

  const getTooltip = () => {
    if (disabled) return null;
    return (
      <StyledTooltipWrapper>
        <Tooltip
          fieldId={fieldId}
          title={tipTitle}
          description={tipDescription}
          tipVisible={tipVisible}
        />
      </StyledTooltipWrapper>
    );
  };

  return (
    <>
      {getTooltip()}
      <StyledTable
        data-testid={`${id}-table-field-table`}
        {...{ border, borderRadius, measure }}
        ref={(ref) => handleTableFieldPageBreakAdjustments(ref, pageBreakPointsPixels, { borderWidth })}
      >
        <div id="table-field-row">
          <TableFieldHeader
            id={id}
            disabled={disabled}
            columns={columns}
            editable={editable}
            {...{
              id,
              disabled,
              columns,
              columnWidths,
              editable,

              //Styles
              fontSize: columnTitleFontSize,
              fontColor: columnTitleFontColor,
              textAlign: columnTitleTextAlign,
              fontStyle: columnTitleFontStyle,
              fontWeight: columnTitleFontWeight,
              textTransform: columnTitleTextTransform,
              textDecoration: columnTitleTextDecoration,
              backgroundColor: columnTitleBackgroundColor,
              padding: columnTitlePadding,
              borderWidth,
              borderColor,
            }}
            onEdit={onColumnEdit}
            onMoveLeft={onColumnMoveLeft}
            onMoveRight={onColumnMoveRight}
            onColumnRefs={onColumnRefs}
          />
        </div>
        {rows.map((row, rowIndex) => (
          <div key={`${row._id}-${rowIndex}`} id="table-field-row">
            <TableFieldRow
              key={`${row._id}-${rowIndex}`}
              tableLookup={rowTableLookup[rowIndex + 1] || {}}
              {...{

                id,
                disabled,
                isMobileView,
                focused,
                row,
                rowIndex,
                columns,
                columnWidths,

                primaryColor,
                dangerColor,
                fontSize,
                fontColor,
                fontStyle,
                fontWeight,
                textAlign,
                textTransform,
                textDecoration,
                textOverflow,
                borderColor,
                borderWidth,
                backgroundColor,
                padding,
              }}
              onCellChange={onCellChange}
              onCellFocus={onCellFocus}
              onCellBlur={onCellBlur}
              onCellKeyPress={onCellKeyPress}
              onCellUpload={onCellUpload}
              onRowAddBelow={onRowAddBelow}
              onRowDuplicate={onRowDuplicate}
              onRowMoveUp={onRowMoveUp}
              onRowMoveDown={onRowMoveDown}
              onRowDelete={onRowDelete}
              onLoadCompleted={onRowLoadCompleted}
            />
          </div>
        ))}
        {disabled || fieldMode === FieldModes.edit ? null : <Button text="Add Row" styles={{ margin: '8px' }} icon={ThemeIcons.misc.add} onClick={onRowCreate} />}
      </StyledTable>
    </>
  );
};

export default TableField;
